/**
 * Liste déroulante (design system, ListeDeroulante) : un bouton qui ouvre une liste d'options
 * (`listbox`) ; flèches, Origine, Fin, première lettre, Entrée pour choisir, Échap pour fermer.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { cx } from '../outils';
import { Icone, type NomIcone } from './Icone';
import { MessageDeChamp } from './MessageDeChamp';

export interface OptionListe<V extends string = string> {
  valeur: V;
  libelle: string;
  description?: string;
  icone?: NomIcone;
  /** Retrait (paquets imbriqués). */
  niveau?: number;
}

export interface ListeDeroulanteProps<V extends string = string> {
  /** Identifiant de l'élément qui nomme la liste (libellé visible). */
  libelleId?: string;
  etiquette?: string;
  options: OptionListe<V>[];
  valeur: V | null;
  onChoisir: (valeur: V) => void;
  exemple?: string;
  desactive?: boolean;
  erreur?: string;
  aide?: string;
  large?: boolean;
  className?: string;
  /** Libellé affiché dans le bouton s'il diffère du libellé de l'option. */
  affichage?: (option: OptionListe<V>) => string;
}

export function ListeDeroulante<V extends string>({
  libelleId,
  etiquette,
  options,
  valeur,
  onChoisir,
  exemple,
  desactive = false,
  erreur,
  aide,
  large = false,
  className,
  affichage,
}: ListeDeroulanteProps<V>) {
  const id = useId();
  const [ouverte, setOuverte] = useState(false);
  const [active, setActive] = useState(0);
  const conteneur = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const choisie = options.find((o) => o.valeur === valeur) ?? null;

  useEffect(() => {
    if (!ouverte) return;
    const exterieur = (e: PointerEvent) => {
      if (!conteneur.current?.contains(e.target as Node)) setOuverte(false);
    };
    document.addEventListener('pointerdown', exterieur, true);
    return () => document.removeEventListener('pointerdown', exterieur, true);
  }, [ouverte]);

  useEffect(() => {
    if (ouverte) {
      liste.current?.querySelectorAll<HTMLElement>('[role="option"]')[active]?.focus();
    }
  }, [ouverte, active]);

  const ouvrir = () => {
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.valeur === valeur),
      ),
    );
    setOuverte(true);
  };
  const fermer = () => {
    setOuverte(false);
    bouton.current?.focus();
  };
  const choisir = (v: V) => {
    onChoisir(v);
    fermer();
  };

  function clavierListe(e: KeyboardEvent<HTMLDivElement>) {
    let i = active;
    switch (e.key) {
      case 'ArrowDown':
        i = Math.min(options.length - 1, active + 1);
        break;
      case 'ArrowUp':
        i = Math.max(0, active - 1);
        break;
      case 'Home':
        i = 0;
        break;
      case 'End':
        i = options.length - 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (options[active]) choisir(options[active].valeur);
        return;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        fermer();
        return;
      case 'Tab':
        setOuverte(false);
        return;
      default:
        if (e.key.length === 1) {
          const lettre = e.key.toLocaleLowerCase('fr');
          const suivants = [...options.keys()].map((k) => (active + 1 + k) % options.length);
          const trouve = suivants.find((k) =>
            options[k]!.libelle.toLocaleLowerCase('fr').startsWith(lettre),
          );
          if (trouve !== undefined) i = trouve;
          break;
        }
        return;
    }
    e.preventDefault();
    setActive(i);
  }

  return (
    <div className="fc-champ-groupe" style={large ? undefined : { display: 'contents' }}>
      <div
        ref={conteneur}
        className={cx('fc-liste-deroulante', large && 'fc-liste-deroulante--large', className)}
      >
        <button
          ref={bouton}
          type="button"
          id={`${id}-bouton`}
          className="fc-liste-deroulante__bouton"
          aria-haspopup="listbox"
          aria-expanded={ouverte}
          aria-controls={ouverte ? `${id}-options` : undefined}
          aria-labelledby={libelleId ? `${libelleId} ${id}-bouton` : undefined}
          aria-label={libelleId ? undefined : etiquette}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={erreur ? `${id}-erreur` : aide ? `${id}-aide` : undefined}
          disabled={desactive}
          onClick={() => (ouverte ? setOuverte(false) : ouvrir())}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              ouvrir();
            }
          }}
        >
          <span className="fc-liste-deroulante__valeur" data-vide={choisie ? undefined : ''}>
            {choisie ? (affichage ? affichage(choisie) : choisie.libelle) : (exemple ?? '')}
          </span>
          <Icone nom="chevron-down" className="fc-liste-deroulante__chevron" />
        </button>
        {ouverte && (
          <div
            ref={liste}
            className="fc-menu fc-liste-deroulante__options"
            role="listbox"
            id={`${id}-options`}
            aria-labelledby={libelleId}
            onKeyDown={clavierListe}
          >
            {options.map((option, i) => (
              <div
                key={option.valeur}
                className="fc-menu__element"
                role="option"
                aria-selected={option.valeur === valeur}
                tabIndex={-1}
                style={
                  option.niveau
                    ? { paddingLeft: `calc(var(--espace-8) + ${option.niveau} * var(--espace-16))` }
                    : undefined
                }
                onMouseEnter={() => setActive(i)}
                onClick={() => choisir(option.valeur)}
              >
                {option.icone && <Icone nom={option.icone} className="fc-menu__icone" />}
                {option.description ? (
                  <span className="fc-menu__texte">
                    <span className="fc-menu__libelle">{option.libelle}</span>
                    <span className="fc-menu__description">{option.description}</span>
                  </span>
                ) : (
                  <span className="fc-menu__libelle">{option.libelle}</span>
                )}
                <Icone nom="check" className="fc-menu__coche" />
              </div>
            ))}
          </div>
        )}
      </div>
      {erreur ? (
        <MessageDeChamp id={`${id}-erreur`} ton="erreur">
          {erreur}
        </MessageDeChamp>
      ) : (
        aide && <MessageDeChamp id={`${id}-aide`}>{aide}</MessageDeChamp>
      )}
    </div>
  );
}
